// Reading ahead what a nav click will need (Board #2594): every nav page's code once the shell is idle,
// and, when the pointer reaches (or the keyboard focuses) a nav item, that page's data as it read it last
// time (the read cache, state/read-cache.ts), so the click shows the page at once.
import * as React from 'react';
import { BUILT_PAGES } from '../app/routes.ts';
import { flattenNav, type NavEntry } from '../modules/platform/index.ts';
import { warmRuntimeSession } from '../modules/identity/index.ts';
import { readCache } from '../modules/platform/index.ts';

const loaded = new Set<string>();

/** Loads a page's code (once); a page that is not built has none. */
export function prefetchPageCode(page: string): void {
  if (loaded.has(page)) return;
  loaded.add(page);
  const built = BUILT_PAGES as Record<string, (() => Promise<unknown>) | undefined>;
  // A page with sub-routes is built under its splat (code/*).
  const load = built[page] ?? built[`${page}/*`];
  void load?.().catch(() => loaded.delete(page));
}

/** Pages whose apps open in the runtime's frame: their click needs the runtime session too. */
const RUNTIME_PAGES = new Set(['apps']);

/** A nav item is about to be clicked: its code and its data (and, for Apps, the runtime sign-in). */
export function prefetchNavItem(page: string, path: string): void {
  prefetchPageCode(page);
  readCache.prefetchPage(path);
  if (RUNTIME_PAGES.has(page)) warmRuntimeSession();
}

const idle = (fn: () => void) => {
  const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
  if (w.requestIdleCallback) w.requestIdleCallback(fn, { timeout: 3000 });
  else setTimeout(fn, 500);
};

/**
 * Once the shell is idle: the runtime sign-in when its nav offers Apps (an app's frame needs it), then
 * the code of every page its nav offers, one after another.
 */
export function usePrefetchPages(entries: NavEntry[]): void {
  const pages = React.useMemo(() => flattenNav(entries).map((p) => p.page), [entries]);
  React.useEffect(() => {
    let stopped = false;
    if (pages.some((p) => RUNTIME_PAGES.has(p))) idle(() => !stopped && warmRuntimeSession());
    const queue = [...pages];
    const next = () => {
      if (stopped) return;
      const page = queue.shift();
      if (!page) return;
      prefetchPageCode(page);
      idle(next);
    };
    idle(next);
    return () => {
      stopped = true;
    };
  }, [pages]);
}
